import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Button } from "../components/ui/button";
import type { Drapeaux } from "./contract";
import { useDrapeaux } from "./queries";

/**
 * La coquille de la console : menu de gauche, bandeau des drapeaux, deconnexion.
 *
 * Elle est posee par chaque page de l'app plutot que par la mise en page racine :
 * `src/root-route.tsx` appartient au template et sert aussi les pages legales, qui
 * doivent rester lisibles sans menu ni session.
 *
 * Le menu porte six modules : le portefeuille, les silhouettes (#145), les cadres (#167),
 * sous les silhouettes comme le veut leur spec, les bobines (#220), sous les cadres avec les
 * autres outils de l'atelier, le coloriage (#151), les histoires (#225), apres le coloriage
 * comme le veut la leur, puis les livres-jeux (#276), l'editeur du livre-jeu. Les suivants (les tests, les produits en ligne) s'ajoutent
 * dans `MODULES` et nulle part ailleurs.
 */

type Module = {
  readonly libelle: string;
  readonly to: string;
  /** Un module annonce mais pas encore ecrit reste visible, en grise. */
  readonly ouvert: boolean;
  /**
   * Vrai : l'entree n'est allumee que sur son adresse exacte. Le portefeuille est a `/`, que
   * toute adresse prolonge ; le coloriage reste allume sur ses cahiers et ses pages, les
   * histoires sur leurs livres et leurs doubles.
   */
  readonly exact: boolean;
};

const MODULES: readonly Module[] = [
  { libelle: "Portefeuille", to: "/", ouvert: true, exact: true },
  { libelle: "Silhouettes", to: "/silhouettes", ouvert: true, exact: true },
  { libelle: "Cadres", to: "/cadres", ouvert: true, exact: true },
  { libelle: "Bobines", to: "/bobines", ouvert: true, exact: true },
  { libelle: "Coloriage", to: "/coloriage", ouvert: true, exact: false },
  { libelle: "Histoires", to: "/histoires", ouvert: true, exact: false },
  { libelle: "Livres-jeux", to: "/livres-jeux", ouvert: true, exact: false },
];

export function ConsoleShell({
  children,
  connecte,
}: {
  children: ReactNode;
  /** false tant que la page principale repond 401 : inutile d'aller lire les drapeaux. */
  connecte: boolean;
}) {
  const drapeaux = useDrapeaux(connecte);

  return (
    // `flex-1` : la coquille descend jusqu'au pied de page (voir `theme.css`, section 1). Les
    // rangees sont nommees pour que, sur telephone, ce soit le contenu qui s'etire et pas
    // la barre du menu.
    <div className="console-shell grid min-h-[70vh] flex-1 grid-rows-[auto_minmax(0,1fr)] md:grid-cols-[13rem_minmax(0,1fr)] md:grid-rows-1">
      <MenuLateral connecte={connecte} />
      {/* 80rem : au-dela, les lignes de « ce qui t'attend » et la colonne du verdict
          s'etirent jusqu'a ne plus se lire d'un seul regard. */}
      <div className="flex w-full min-w-0 max-w-[80rem] flex-col gap-5 p-4 md:p-6">
        {drapeaux.data ? <BandeauDrapeaux drapeaux={drapeaux.data} /> : null}
        {children}
      </div>
    </div>
  );
}

function MenuLateral({ connecte }: { connecte: boolean }) {
  const queryClient = useQueryClient();

  const seDeconnecter = async (): Promise<void> => {
    try {
      await fetch("/api/auth/sign-out", { method: "POST" });
    } finally {
      // Le cache vide, les lectures repartent et repondent 401 : la page de connexion
      // revient d'elle-meme. Aucune navigation forcee, rien a recharger.
      queryClient.clear();
    }
  };

  // Le menu est sur des tokens de surface, pas sur `primary` : `primary` s'inverse avec le
  // theme, et en sombre le menu devenait le bloc le plus lumineux de l'ecran alors qu'il
  // en est le plus vide. Une carte avec un filet se tient dans les deux themes.
  return (
    <nav
      aria-label="Modules de la console"
      className="flex flex-row flex-wrap items-center gap-2 border-b border-border bg-card p-4 text-card-foreground md:flex-col md:flex-nowrap md:items-stretch md:border-r md:border-b-0"
    >
      {/* « Lab » et non le nom complet : l'en-tete du template porte deja `appConfig.name`
          juste au-dessus, et le repeter dans le menu ne dit rien de plus. */}
      <span className="font-semibold md:mb-4 md:text-lg">Lab</span>
      {/* Sur telephone, les modules ont leur propre rangee, sous « Lab » et « Sortir » : a
          trois modules, une seule rangee faisait 440 px pour un ecran de 390. Un module de
          plus passe a la ligne, sans rien cacher. Sur grand ecran, la colonne ne change pas. */}
      <div className="order-last flex w-full flex-wrap gap-1 md:order-none md:w-auto md:flex-col md:gap-2">
        {MODULES.map((module) =>
          module.ouvert ? (
            <Link
              key={module.to}
              to={module.to}
              // Sans `includeSearch`, `?mode=serre-livres` (#337) eteindrait l'entree du menu.
              activeOptions={{ exact: module.exact, includeSearch: false }}
              activeProps={{ className: "bg-muted font-medium" }}
              className="whitespace-nowrap rounded-md px-3 py-1.5 text-sm hover:bg-muted"
            >
              {module.libelle}
            </Link>
          ) : (
            <span
              key={module.to}
              className="whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-muted-foreground"
            >
              {module.libelle}
            </span>
          ),
        )}
      </div>
      {connecte ? (
        <Button
          className="ml-auto whitespace-nowrap md:mt-auto md:ml-0"
          onClick={() => void seDeconnecter()}
          size="sm"
          variant="outline"
        >
          {/* « Sortir » sur telephone, comme la maquette `SilhouettesMobile.dc.html`. Un seul
              des deux libelles est affiche, donc un seul est lu. */}
          <span className="md:hidden">Sortir</span>
          <span className="hidden md:inline">Se déconnecter</span>
        </Button>
      ) : null}
    </nav>
  );
}

/**
 * Les trois drapeaux du Lab. Rien ne s'affiche quand tout est baissé et que lab-tools
 * répond : un bandeau permanent qui dit « tout va bien » finit par ne plus se lire.
 */
export function BandeauDrapeaux({ drapeaux }: { drapeaux: Drapeaux }) {
  const leves: string[] = [];
  if (drapeaux.stopAll) leves.push("arrêt général du Lab (stop_all)");
  if (drapeaux.pauseSpend) leves.push("dépenses en pause (pause_spend)");
  if (drapeaux.pausePublish) leves.push("publications en pause (pause_publish)");

  if (leves.length === 0 && drapeaux.joignable) return null;

  return (
    <p className="rounded-md border border-border bg-muted px-4 py-2 text-sm" role="status">
      {leves.length > 0
        ? `Drapeaux levés : ${leves.join(", ")}.`
        : "Drapeaux du Lab inconnus : lab-tools n'a pas répondu."}
    </p>
  );
}
